import { useState } from "react";
import { Button, Input, Select, DatePicker, TimePicker, message } from "antd";
import dayjs from "dayjs";
import { formSubmissions } from "../../../api/UserRequest";

const { TextArea } = Input;
const { Option } = Select;

function FormQrCodeInterfaceTheme({ card }) {
    const [formData, setFormData] = useState(() => {
        // Initialize form data based on the sections
        const initialData = {};
        if (card?.data?.sections) {
            card.data.sections.forEach(section => {
                const key = section.key || section.label.toLowerCase().replace(/\s+/g, '-');
                initialData[key] = section.type === "Multi Select" ? [] : '';
            });
        }
        return initialData;
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const handleChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
        // Clear error when user starts typing
        if (errors[key]) {
            setErrors(prev => ({ ...prev, [key]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (card?.data?.sections) {
            card.data.sections.forEach(section => {
                const key = section.key || section.label.toLowerCase().replace(/\s+/g, '-');
                const value = formData[key];
                
                if (section.required) {
                    if (!value || (Array.isArray(value) && value.length === 0)) {
                        newErrors[key] = section.errorMessage || `${section.label} is required`;
                    }
                }
                
                // Email validation
                if (section.type === "E-mail" && value) {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(value)) {
                        newErrors[key] = "Please enter a valid email address";
                    }
                }
            });
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            message.error('Please fix the errors before submitting');
            return;
        }

        setLoading(true);
        try {
            // Prepare the data to send
            const submissionData = {
                formId:card?._id,
                formType: card?.formType || 'form_qrcode',
                data: {}
            };

            // Map form data with labels
            if (card?.data?.sections) {
                card.data.sections.forEach(section => {
                    const key = section.key || section.label.toLowerCase().replace(/\s+/g, '-');
                    submissionData.data[key] = {
                        label: section.label,
                        value: formData[key],
                        type: section.type
                    };
                });
            }

            // Send to backend
            // const response = await axios.post('/api/form-submissions', submissionData);
            const response = await formSubmissions(submissionData)
            
            if (response.data.success) {
                message.success('Form submitted successfully!');
                // Reset form
                setFormData(() => {
                    const resetData = {};
                    if (card?.data?.sections) {
                        card.data.sections.forEach(section => {
                            const key = section.key || section.label.toLowerCase().replace(/\s+/g, '-');
                            resetData[key] = section.type === "Multi Select" ? [] : '';
                        });
                    }
                    return resetData;
                });
            }
        } catch (error) {
            console.error('Form submission error:', error);
            message.error(error.response?.data?.message || 'Failed to submit form. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const renderInput = (section) => {
        const key = section.key || section.label.toLowerCase().replace(/\s+/g, '-');
        const commonProps = { 
            style: { width: '100%' },
            status: errors[key] ? 'error' : ''
        };

        switch (section.type) {
            case 'One-Line':
                return (
                    <Input
                        {...commonProps}
                        placeholder={section.placeholder || `Enter ${section.label}`}
                        value={formData[key] || ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        className="rounded"
                    />
                );

            case 'E-mail':
                return (
                    <Input
                        {...commonProps}
                        type="email"
                        placeholder={section.placeholder || `Enter ${section.label}`}
                        value={formData[key] || ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        className="rounded"
                    />
                );

            case 'Number':
                return (
                    <Input
                        {...commonProps}
                        type="number"
                        placeholder={section.placeholder || `Enter ${section.label}`}
                        value={formData[key] || ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        className="rounded"
                    />
                );

            case 'Multi-Line':
                return (
                    <TextArea
                        {...commonProps}
                        placeholder={section.placeholder || `Enter ${section.label}`}
                        value={formData[key] || ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        className="rounded"
                        rows={4}
                    />
                );

            case 'DropDown':
                return (
                    <Select
                        {...commonProps}
                        placeholder={section.placeholder || `Select ${section.label}`}
                        value={formData[key] || undefined}
                        onChange={(value) => handleChange(key, value)}
                        className="rounded"
                    >
                        {section.options?.map((option) => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                );

            case 'Date':
                return (
                    <DatePicker
                        {...commonProps}
                        value={formData[key] ? dayjs(formData[key]) : null}
                        onChange={(date) => handleChange(key, date ? date.toISOString() : '')}
                        placeholder={section.placeholder || `Select ${section.label}`}
                        className="rounded"
                    />
                );

            case 'Time':
                return (
                    <TimePicker
                        {...commonProps}
                        value={formData[key] ? dayjs(formData[key], 'HH:mm') : null}
                        onChange={(time) => handleChange(key, time ? time.format('HH:mm') : '')}
                        placeholder={section.placeholder || "Select time"}
                        format="HH:mm"
                        className="rounded"
                    />
                );

            case 'Multi Select':
                return (
                    <Select
                        {...commonProps}
                        mode="multiple"
                        placeholder={section.placeholder || `Select ${section.label}`}
                        value={formData[key] || []}
                        onChange={(value) => handleChange(key, value)}
                        className="rounded"
                    >
                        {section.options?.map((option) => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                );

            default:
                return null;
        }
    };

    if (!card) {
        return <div className="text-center p-8">Loading form...</div>;
    }

    return (
        <div className="flex md:py-4 min-h-screen p-2 justify-center bg-gray-200">
            <div className="flex max-w-[393px]  rounded-lg text-white overflow-y-auto scrollbar-hide bg-white">
                <div className="text-black w-[385px] p-3">
                    {/* Profile Section */}
                    {card?.data?.isProfileVisible && card?.profileImage && (
                        <div className="flex justify-center items-center rounded-lg p-8">
                            <img
                                src={card?.profileImage || "/default-profile.svg"}
                                alt="Profile"
                                className="rounded-full h-20 w-20"
                            />
                        </div>
                    )}

                    <div>
                        {/* Header Section */}
                        <div className="flex flex-col justify-evenly items-center pb-5 max-w-full">
                            <p className="text-3xl font-bold break-words">{card?.name}</p>
                            <p className="text-xl text-gray-600 break-words">{card?.data?.heading}</p>
                            <p className="text-xl text-gray-600 break-words">{card?.data?.subHeading}</p>
                        </div>

                        {/* Form Section */}
                        <div className="border-none mb-2 bg-[#F2F3F5] px-4 py-8 rounded-lg">
                            {/* Form Title and Description */}
                            {card?.data?.otherDetailsEnabled && (
                                <div className="flex flex-col justify-evenly items-center w-full h-full">
                                    <h1 className="text-2xl font-bold text-slate-700 flex justify-center w-full h-full break-words">
                                        {card?.data?.formTitle || "Contact Form"}
                                    </h1>
                                    <div className="flex flex-col justify-evenly items-center py-2">
                                        <p className="text-lg text-slate-600 break-words">
                                            {card?.data?.formDescription || "We would love to hear from you."}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Dynamic Form Fields */}
                            <div className="lg:grid grid-cols-1 lg:gap-4 pt-4">
                                {card?.data?.sections?.map((section) => {
                                    const key = section.key || section.label.toLowerCase().replace(/\s+/g, '-');
                                    return (
                                        <div key={key} className="mb-4">
                                            <label className="text-sm break-words">
                                                {section.label}
                                                {section.required && (
                                                    <span style={{ color: 'red' }}> *</span>
                                                )}
                                            </label>
                                            {renderInput(section)}
                                            {errors[key] && (
                                                <div className="text-red-500 text-xs mt-1 break-words">
                                                    {errors[key]}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Submit Button */}
                            <Button
                                className="w-full h-full mt-6 bg-custom-gradient rounded-full"
                                type="primary"
                                loading={loading}
                                onClick={handleSubmit}
                            >
                                Submit
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FormQrCodeInterfaceTheme;